import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateIntakeLinkInput,
  WritableSections,
} from "@shared/validation/intake.schema";
import { apiClient, ApiError } from "./client";

export interface IntakeLinkSummary {
  id: string;
  childId: string;
  tokenPrefix: string;
  proxyName: string | null;
  proxyEmail: string | null;
  writableSections: WritableSections;
  expiresAt: string | null;
  isActive: boolean;
  singleUse: boolean;
  submittedAt: string | null;
  submitCount: number;
  lastAccessedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
}

export interface CreateIntakeLinkResponse {
  link: IntakeLinkSummary;
  /** Raw token — returned ONCE on create. */
  token: string;
}

const intakeKey = (childId: string) => ["intake-links", childId] as const;

export function useIntakeLinks(childId: string | undefined) {
  return useQuery<IntakeLinkSummary[], ApiError>({
    queryKey: childId ? intakeKey(childId) : ["intake-links", "__none__"],
    queryFn: () => {
      if (!childId) throw new Error("useIntakeLinks: childId required");
      return apiClient<IntakeLinkSummary[]>(
        `/api/intake-links?childId=${encodeURIComponent(childId)}`,
      );
    },
    enabled: Boolean(childId),
  });
}

export function useCreateIntakeLink(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<CreateIntakeLinkResponse, ApiError, CreateIntakeLinkInput>({
    mutationFn: (vars) =>
      apiClient<CreateIntakeLinkResponse>("/api/intake-links", {
        method: "POST",
        body: JSON.stringify(vars),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intakeKey(childId) }),
  });
}

export function useRevokeIntakeLink(childId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ revoked: true }, ApiError, string>({
    mutationFn: (linkId) =>
      apiClient<{ revoked: true }>(`/api/intake-links/${linkId}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: intakeKey(childId) }),
  });
}
